import { AnyString } from '../../../utils/types';
import IPostMessage from '../../IPostMessage';
import validate from '../../Validate/Validate';
import { IProprietaryTimer, ProprietaryTimerType } from '../helpers/ProprietaryTimerHelper';
import { ITimer, TimerType } from '../helpers/TimerHelper';
import IPower, { IScheduledRebootAction, IScheduledRebootRule, IScheduledRebootRuleRaw, ShortWeekdayType } from './IPower';
import { coalesceWeekdayType, convertBCWeekdayToNumber, convertNumberToWeekday } from './PowerHelper';

/**
 * The `sos.management.power` API groups together methods related to the power state of the device. Such as rebooting, shutting down,
 * setting timers.
 *
 * <details>
 *     <summary>Power Management Capabilities</summary>
 * 		| Capability | Description |
 * 		|:------------|:-------------|
 * 		| `SYSTEM_REBOOT` | If device supports system reboot power action |
 * 		| `APP_RESTART` | If device supports app restart power action |
 * 		| `TIMERS_PROPRIETARY` | If device supports proprietary timers |
 * 		| `TIMERS_NATIVE` | If device supports native timers |
 * 		| `SCHEDULE_POWER_ACTION` | If device supports scheduled power actions |
 *
 * 		If you want to check if the device supports those capabilities, use [`sos.management.supports()`](https://developers.signageos.io/sdk/sos_management/#supports).
 * </details>
 */
export default class Power implements IPower {
	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<any>,
	) {}

	/**
	 * The `systemReboot()` method initializes a system reboot.
	 *
	 * @returns {Promise<void>} Resolves when the system reboot is initiated.
	 * @since 3.0.0
	 *
	 * @example // {@link https://github.com/signageos/applet-examples/tree/master/examples/management-js-api/system-reboot | Applet Example for System Reboot}
	 *
	 * @example
	 * await sos.management.power.systemReboot();
	 */
	public async systemReboot(): Promise<void> {
		await this.postMessage({
			type: this.getMessage('reboot_system'),
		});
	}

	/**
	 * The `appRestart()` method initializes a restart of the signageOS app.
	 *
	 * @returns {Promise<void>} Resolves when the app restart is initiated.
	 * @since 3.0.0
	 *
	 * @example
	 * await sos.management.power.appRestart();
	 */
	public async appRestart(): Promise<void> {
		await this.postMessage({
			type: this.getMessage('restart_app'),
		});
	}

	/**
	 * The `getTimers()` method returns a list of currently set native timers.
	 *
	 * @returns {Promise<ITimer[]>} Resolves with an array of timers.
	 * @since 4.0.0
	 *
	 * @example
	 * await sos.management.power.getTimers();
	 */
	public async getTimers(): Promise<ITimer[]> {
		const { timers } = await this.postMessage({
			type: this.getMessage('get_timers'),
		});
		return timers;
	}

	/**
	 * The `setTimer()` method creates or updates a native timer.
	 *
	 * @param type The type of the timer (`TIMER_1`, ..., `TIMER_7`).
	 * @param timeOn The time when the device should turn on.
	 * @param timeOff The time when the device should turn off.
	 * @param {ShortWeekdayType} weekdays The days of the week when the timer should be active (`mon`, ..., `sun`).
	 * @param volume The volume level set when the device is turned on.
	 * @return {Promise<void>} Resolves when the timer is set.
	 * @throws {Error} If the timer type is invalid.
	 * @throws {Error} If the time format is incorrect.
	 * @throws {Error} If the weekdays array contains an invalid weekday.
	 * @throws {Error} If the `volume` parameter is not a number between 0 and 100.
	 * @throws {Error} If the `timeOn` or `timeOff` parameters do not match the expected time format (`HH:mm:ss`).
	 * @throws {Error} If the `weekdays` parameter is not an array of strings.
	 * @throws {Error} If the `type` parameter is not a valid timer type (e.g., `TIMER_1`, `TIMER_2`, ..., `TIMER_7`).
	 * @since 3.0.0
	 *
	 * @example // {@link https://github.com/signageos/applet-examples/tree/master/examples/management-js-api/timer | Applet Example for Native Timers}
	 *
	 * @example
	 * await sos.management.power.setTimer("TIMER_1", "08:00:00", "22:00:00", ["mon", "tue", "wed", "thu", "fri", "sat", "sun"], 30);
	 */
	public async setTimer(
		type: keyof typeof TimerType,
		timeOn: string | null,
		timeOff: string | null,
		weekdays: (ShortWeekdayType | AnyString)[], // TODO: remove AnyString in the next major release
		volume: number,
	): Promise<void> {
		if (typeof TimerType[type as keyof typeof TimerType] === 'undefined') {
			throw new Error('Invalid timer type');
		}
		validate({ timeOn })
			.canBeNull()
			.required()
			.string()
			.matchRegExp(/\d{2}:\d{2}:\d{2}/)
			.timerTime();
		validate({ timeOff })
			.canBeNull()
			.required()
			.string()
			.matchRegExp(/\d{2}:\d{2}:\d{2}/)
			.timerTime();
		validate({ weekdays }).required().array('string');
		validate({ volume }).required().number().min(0).max(100);

		const shortWeekdays = weekdays.map(coalesceWeekdayType);

		await this.postMessage({
			type: this.getMessage('set_timer'),
			timerType: type,
			timeOn,
			timeOff,
			weekdays: shortWeekdays,
			volume,
		});
	}

	/**
	 * The `unsetTimer()` method removes the specified native timer.
	 *
	 * @param type The type of the timer (`TIMER_1`, ..., `TIMER_7`).
	 * @returns {Promise<void>} Resolves when the timer is removed.
	 * @throws {Error} If the timer type is invalid.
	 * @since 4.0.0
	 *
	 * @example
	 * await sos.management.power.unsetTimer("TIMER_2");
	 */
	public async unsetTimer(type: keyof typeof TimerType): Promise<void> {
		if (typeof TimerType[type as keyof typeof TimerType] === 'undefined') {
			console.warn('Invalid timer type');
		}
		await this.setTimer(type, null, null, [], 0);
	}

	/**
	 * The `getProprietaryTimers()` method returns a list of currently set
	 * [proprietary timers](https://docs.signageos.io/hc/en-us/articles/4416384202642-Timers#h_01HCD14GEDP96AZV58NRSN2HNQ).
	 *
	 * @returns {Promise<IProprietaryTimer[]>} Resolves with an array of proprietary timers.
	 * @throws {Error} If the timers cannot be retrieved.
	 * @since 5.10.0
	 *
	 * @example
	 * const timers = await sos.management.power.getProprietaryTimers();
	 */
	public async getProprietaryTimers(): Promise<IProprietaryTimer[]> {
		const { timers } = await this.postMessage({
			type: this.getMessage('get_proprietary_timers'),
		});
		return timers;
	}

	/**
	 * The `setProprietaryTimer()` method creates or updates a
	 * [proprietary timer](https://docs.signageos.io/hc/en-us/articles/4416384202642-Timers#h_01HCD14GEDP96AZV58NRSN2HNQ).
	 *
	 * @param type The type of the timer (`TIMER_1`, ..., `TIMER_7`).
	 * @param timeOn The time when the device should turn on.
	 * @param timeOff The time when the device should turn off.
	 * @param weekdays The days of the week when the timer should be active (`mon`, ..., `sun`).
	 * @param keepAppletRunning If `true`, the applet will be kept running when the timer is active on certain devices.
	 * @return {Promise<void>} Resolves when the proprietary timer is set.
	 * @throws {Error} If the timer type is invalid or if the time format is incorrect.
	 * @throws {Error} If the weekdays array contains an invalid weekday.
	 * @throws {Error} If the `keepAppletRunning` parameter is not a boolean.
	 * @throws {Error} If the `timeOn` or `timeOff` parameters do not match the expected time format (`HH:mm:ss`).
	 * @throws {Error} If the `weekdays` parameter is not an array of strings.
	 * @throws {Error} If the `weekdays` array contains an invalid weekday.
	 * @throws {Error} If the `timeOn` or `timeOff` parameters do not match the expected time format (`HH:mm:ss`).
	 * @throws {Error} If the `type` parameter is not a valid timer type (e.g., `TIMER_1`, `TIMER_2`, ..., `TIMER_7`).
	 * @since 5.10.0
	 *
	 * @example
	 * await sos.management.power.setProprietaryTimer("TIMER_2", "08:00:00", "22:00:00", ["mon", "tue", "wed", "thu", "fri", "sat", "sun"], false);
	 */
	public async setProprietaryTimer(
		type: ProprietaryTimerType,
		timeOn: string | null,
		timeOff: string | null,
		weekdays: (ShortWeekdayType | AnyString)[], // TODO: remove AnyString in the next major release
		keepAppletRunning: boolean = false,
	): Promise<void> {
		validate({ type })
			.required()
			.string()
			.matchRegExp(/^TIMER_\d+$/);
		validate({ timeOn })
			.canBeNull()
			.required()
			.string()
			.matchRegExp(/\d{2}:\d{2}:\d{2}/)
			.timerTime();
		validate({ timeOff })
			.canBeNull()
			.required()
			.string()
			.matchRegExp(/\d{2}:\d{2}:\d{2}/)
			.timerTime();
		validate({ weekdays }).required().array('string');
		validate({ keepAppletRunning }).required().boolean();

		const shortWeekdays = weekdays.map(coalesceWeekdayType);

		await this.postMessage({
			type: this.getMessage('set_proprietary_timer'),
			timerType: type,
			timeOn,
			timeOff,
			weekdays: shortWeekdays,
			keepAppletRunning,
		});
	}

	/**
	 * The `unsetProprietaryTimer()` method removes the specified
	 * [proprietary timer](https://docs.signageos.io/hc/en-us/articles/4416384202642-Timers#h_01HCD14GEDP96AZV58NRSN2HNQ).
	 *
	 * @param type The type of the timer (`TIMER_1`, ..., `TIMER_7`).
	 * @returns {Promise<void>} Resolves when the proprietary timer is removed.
	 * @throws {Error} If the timer type is invalid.
	 * @since 5.10.0
	 *
	 * @example
	 * await sos.management.power.unsetProprietaryTimer("TIMER_2");
	 */
	public async unsetProprietaryTimer(type: ProprietaryTimerType): Promise<void> {
		try {
			await this.postMessage({
				type: this.getMessage('delete_proprietary_timer'),
				timerType: type,
			});
		} catch {
			// Use the failover method if the delete fails, which is supported in older front-display versions
			// It can be removed in some front-applet major version
			await this.setProprietaryTimer(type, null, null, []);
		}
	}

	/**
	 * Removes all scheduled reboot rules from the device.
	 *
	 * @returns {Promise<void>} Resolves when all scheduled reboots are cleared.
	 * @since 8.1.0
	 *
	 * @example
	 * await sos.management.power.clearScheduledReboots();
	 */
	public async clearScheduledReboots(): Promise<void> {
		await this.postMessage({
			type: this.getMessage('clear_scheduled_reboots'),
		});
	}

	/**
	 * Returns all scheduled reboot rules on the device set by the `setScheduledReboot()` method.
	 *
	 * @returns {Promise<IScheduledRebootAction[]>} Resolves with an array of scheduled reboot actions.
	 * @since 8.1.0
	 *
	 * @example
	 * await sos.management.power.getScheduledReboots();
	 */
	public async getScheduledReboots(): Promise<IScheduledRebootAction<IScheduledRebootRule>[]> {
		const { actions }: { actions: IScheduledRebootAction<IScheduledRebootRuleRaw>[] } = await this.postMessage({
			type: this.getMessage('get_scheduled_reboots'),
		});

		// We need to convert the values of days back to a string representation
		const outputActions = actions.map(
			(action): IScheduledRebootAction<IScheduledRebootRule> => ({
				...action,
				rule: {
					...action.rule,
					weekdays: action.rule.weekdays.map(convertNumberToWeekday),
					shortWeekdays: action.rule.weekdays.map(coalesceWeekdayType),
				},
			}),
		);

		return outputActions;
	}

	/**
	 * Removes scheduled reboot rule from the device (if it exists).
	 * @param id {string} ID of the rule to be removed.
	 * @throws {Error} If the `id` is not a string or is empty.
	 * @returns {Promise<void>} Resolves when the scheduled reboot is removed.
	 * @since 8.1.0
	 *
	 * @example
	 * // Get scheduled reboots
	 * const scheduledReboots = await sos.management.power.getScheduledReboots();
	 *
	 * // Remove scheduled reboot on the first position
	 * await sos.management.power.removeScheduledReboot(scheduledReboots[0].id);
	 */
	public async removeScheduledReboot(id: string): Promise<void> {
		validate({ id }).required().string();
		await this.postMessage({
			type: this.getMessage('remove_scheduled_reboot'),
			id,
		});
	}

	/**
	 * Schedule an automatic reboot on the device. Calling this function will create one rule.
	 * It is possible to set multiple rules, which can be later obtained by the `getScheduledReboots` function.
	 *
	 * :::note
	 * - Setting a new scheduled reboot on the device might take up to 10 minutes to show in the Box.
	 * - Every new scheduled reboot rule gets a unique identifier generated by the device, which might be later returned by `getScheduledReboots()`.
	 * :::
	 *
	 * @param weekdays {@link WeekdayType[]} Array of weekdays when the reboot should be executed.
	 * @param time {string} Time when the reboot should be executed. Format is `HH:mm:ss`.
	 * @throws {Error} If the `weekdays` parameter is not an array of strings.
	 * @throws {Error} If the `time` parameter does not match the expected time format (`HH:mm:ss`).
	 * @throws {Error} If the `weekdays` array contains an invalid weekday.
	 * @throws {Error} If the `time` parameter is not a string or does not match the expected format (`HH:mm:ss`).
	 * @throws {Error} If the `weekdays` array is empty or contains invalid values.
	 * @returns {Promise<void>} Resolves when the scheduled reboot is set.
	 * @since 8.1.0
	 *
	 * @example
	 * // Schedule reboot every Monday at 3:00 AM
	 * await sos.management.power.setScheduledReboot(["MONDAY"], "03:00:00");
	 *
	 * // Schedule reboot every Monday and Friday at 7:30 PM / 19:30
	 * await sos.management.power.setScheduledReboot(["MONDAY", "FRIDAY"], "19:30:00");
	 */
	public async setScheduledReboot(
		weekdays: (ShortWeekdayType | AnyString)[], // TODO: remove AnyString in the next major release
		time: string,
	): Promise<void> {
		validate({ weekdays }).required().array('string');

		// Days in week need to be converted to specific numbers to work properly
		const weekdayAsNumbers = weekdays.map(convertBCWeekdayToNumber);

		validate({ time })
			.required()
			.string()
			.matchRegExp(/\d{2}:\d{2}:\d{2}/)
			.timerTime();

		const rule: IScheduledRebootRuleRaw = {
			weekdays: weekdayAsNumbers,
			time,
		};

		await this.postMessage({
			type: this.getMessage('set_scheduled_reboot'),
			rule,
		});
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + name;
	}
}
